import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseMutationResult,
} from "@tanstack/react-query";

import {
  type ActivityNode,
  type BulkVerdictResult,
  type ConfigActivitiesResponse,
  type ConfigActivityRow,
  type ConfigNode,
  type ConfigServerOverview,
  type ExtractionRefreshStatus,
  type ExtractionSummary,
  type ExtractionSummaryQuery,
  type VerdictItem,
  getConfigServerOverview,
  getExtractionRefresh,
  getExtractionSummary,
  listConfigActivities,
  getExtractionArtifact,
  listExecutionActivityAreas,
  getConfigActivityStatuses,
  type ExecutionActivitiesQuery,
  setScopeVerdicts,
  startExtractionRefresh,
} from "@/api/config-server-stages";

import { LIST_PAGE_SIZE, LIST_QUERY_OPTIONS } from "@/lib/config-pagination";
import type { ScopeVerdict } from "@/lib/org-structure-tree";

export const stageKeys = {
  all: (projectId: string) => ["config-server", "stages", projectId] as const,
  overview: (projectId: string) =>
    ["config-server", "stages", projectId, "overview"] as const,
  extractionSummary: (projectId: string, query?: ExtractionSummaryQuery) =>
    query
      ? ([
          "config-server",
          "stages",
          projectId,
          "extraction-summary",
          query,
        ] as const)
      : (["config-server", "stages", projectId, "extraction-summary"] as const),
  activities: (projectId: string, node: ActivityNode, artifactId?: string) =>
    artifactId
      ? ([
          "config-server",
          "stages",
          projectId,
          "activities",
          node,
          { artifact_id: artifactId },
        ] as const)
      : (["config-server", "stages", projectId, "activities", node] as const),
};

const LIVE_INTERVAL_MS = 3000;

export interface ReadyExtraction {
  artifactId?: string | null;
  executionId?: string | null;
}

/** Depois do complete, só o ready da mesma extração confirma seus números. */
export function matchesReadyExtraction(
  data: ExtractionSummary | undefined,
  expected: ReadyExtraction,
): boolean {
  return (
    !!data?.artifact_id &&
    data.artifact_status === "ready" &&
    !!(expected.artifactId || expected.executionId) &&
    (!expected.artifactId || data.artifact_id === expected.artifactId) &&
    (!expected.executionId || data.execution_id === expected.executionId)
  );
}

/** Só consulta de novo enquanto há execução em voo; parado, o cache normal basta. */
export function overviewRefetchInterval(
  data?: ConfigServerOverview,
): number | false {
  return data?.active_run ? LIVE_INTERVAL_MS : false;
}

export function useConfigServerOverview(
  projectId: string | undefined,
  live = false,
) {
  return useQuery({
    queryKey: stageKeys.overview(projectId ?? ""),
    queryFn: () => getConfigServerOverview(projectId!),
    enabled: !!projectId,
    refetchInterval: (query) =>
      live ? LIVE_INTERVAL_MS : overviewRefetchInterval(query.state.data),
  });
}

export function useExtractionSummary(
  projectId: string | undefined,
  live: boolean,
  expectedReady?: ReadyExtraction,
  awaitReady = false,
) {
  // Se outra run terminar entre snapshots, a leitura explícita ainda confirma
  // o resultado esperado sem aceitar o latest-ready de uma execução diferente.
  const selection = expectedReady
    ? {
        artifact_id: expectedReady.artifactId ?? undefined,
        execution_id: expectedReady.executionId ?? undefined,
      }
    : undefined;
  return useQuery({
    queryKey: stageKeys.extractionSummary(projectId ?? "", selection),
    queryFn: () => getExtractionSummary(projectId!, selection),
    enabled: !!projectId,
    refetchInterval: (query) =>
      live ||
      (awaitReady && query.state.data?.artifact_status !== "ready") ||
      (expectedReady &&
        !matchesReadyExtraction(query.state.data, expectedReady))
        ? LIVE_INTERVAL_MS
        : false,
  });
}

export function useExtractionArtifact(
  projectId: string | undefined,
  live = false,
  awaitReady = false,
) {
  return useQuery({
    queryKey: [...stageKeys.all(projectId ?? ""), "extraction-artifact"],
    queryFn: () => getExtractionArtifact(projectId!),
    enabled: !!projectId,
    refetchInterval: (query) =>
      live || (awaitReady && query.state.data?.artifact_status !== "ready")
        ? LIVE_INTERVAL_MS
        : false,
  });
}
export function useConfigActivities(
  projectId: string | undefined,
  node: ActivityNode,
  query: Omit<ExecutionActivitiesQuery, "cursor" | "limit"> = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [...stageKeys.activities(projectId ?? "", node), query],
    queryFn: ({ pageParam }) =>
      listConfigActivities(projectId!, node, {
        ...query,
        cursor: pageParam,
        limit: LIST_PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!projectId && enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
export function useExecutionActivityAreas(
  projectId: string,
  node: ActivityNode,
  enabled = true,
) {
  return useQuery({
    queryKey: [...stageKeys.activities(projectId, node), "areas"],
    queryFn: () => listExecutionActivityAreas(projectId, node),
    enabled,
    ...LIST_QUERY_OPTIONS,
  });
}
export function useActivityStatuses(
  projectId: string,
  node: ActivityNode,
  codes: string[],
  live: boolean,
) {
  return useQuery({
    queryKey: [...stageKeys.all(projectId), "activity-status", node, codes],
    queryFn: () => getConfigActivityStatuses(projectId, node, codes),
    enabled: !!codes.length,
    refetchInterval: live ? LIVE_INTERVAL_MS : false,
    ...LIST_QUERY_OPTIONS,
  });
}

export interface VerdictMutation {
  node: ConfigNode;
  verdict: ScopeVerdict;
  items: VerdictItem[];
  /** Apenas proveniência do cache; não faz parte do POST do servidor. */
  artifactId?: string;
}

interface DispatchedVerdict extends VerdictMutation {
  /** Capturado no envio para que trocar de projeto não redirecione a decisão. */
  projectId: string | undefined;
}

export interface VerdictResult extends BulkVerdictResult {
  previousVerdicts?: Map<ScopeVerdict, string[]>;
}

interface VerdictContext {
  snapshots: { key: QueryKey; previous: ConfigActivitiesResponse }[];
  release: () => void;
}

// Grupos de Desfazer alteram o mesmo cache: cada projeto tem uma fila
// por QueryClient, incluindo onMutate, gravação, rollback e atualização das leituras.
const verdictLanes = new WeakMap<QueryClient, Map<string, Promise<void>>>();

async function acquireVerdictLane(
  client: QueryClient,
  projectId: string,
): Promise<() => void> {
  let lanes = verdictLanes.get(client);
  if (!lanes) {
    lanes = new Map();
    verdictLanes.set(client, lanes);
  }
  const previous = lanes.get(projectId);
  let finish!: () => void;
  const current = new Promise<void>((resolve) => {
    finish = resolve;
  });
  lanes.set(projectId, current);
  await previous;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (lanes.get(projectId) === current) lanes.delete(projectId);
    finish();
  };
}

/** Cada código volta uma vez para a decisão que tinha antes do movimento. */
export function groupByPreviousVerdict(
  rows: ConfigActivityRow[],
  codes: string[],
): Map<ScopeVerdict, string[]> {
  const verdictOf = new Map(
    rows.map((row) => [row.activity_code, row.scope_verdict] as const),
  );
  const groups = new Map<ScopeVerdict, string[]>();
  for (const code of new Set(codes)) {
    const previous = verdictOf.get(code) ?? "pending";
    const group = groups.get(previous) ?? [];
    group.push(code);
    groups.set(previous, group);
  }
  return groups;
}

function matchesVerdictArtifact(
  key: QueryKey,
  prefixLength: number,
  artifactId?: string,
): boolean {
  if (!artifactId) return key.length === prefixLength;
  const params = key[prefixLength];
  return (
    typeof params === "object" &&
    params !== null &&
    "artifact_id" in params &&
    params.artifact_id === artifactId
  );
}

export function useScopeVerdicts(
  projectId: string | undefined,
): UseMutationResult<VerdictResult, unknown, VerdictMutation, VerdictContext> {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    VerdictResult,
    unknown,
    DispatchedVerdict,
    VerdictContext
  >({
    mutationKey: ["config-server", "scope-verdicts", projectId],
    mutationFn: (vars) => {
      if (!vars.projectId)
        return Promise.reject(new Error("Projeto não encontrado."));
      return setScopeVerdicts(
        vars.projectId,
        vars.node,
        vars.verdict,
        vars.items,
      );
    },
    onMutate: async (vars) => {
      if (!vars.projectId) return undefined;
      const release = await acquireVerdictLane(queryClient, vars.projectId);
      const context: VerdictContext = { snapshots: [], release };
      try {
        // A cascata organizacional é calculada pelo servidor, sem edição local.
        if (vars.node === "org_structure") return context;
        const prefix = stageKeys.activities(vars.projectId, vars.node);
        const queries = queryClient
          .getQueryCache()
          .findAll({ queryKey: prefix })
          .filter((query) =>
            matchesVerdictArtifact(
              query.queryKey,
              prefix.length,
              vars.artifactId,
            ),
          );
        await Promise.all(
          queries.map((query) =>
            queryClient.cancelQueries({
              queryKey: query.queryKey,
              exact: true,
            }),
          ),
        );
        const codes = new Set(
          vars.items
            .filter((item) => item.item_type === "activity")
            .map((item) => item.item_key),
        );
        for (const query of queries) {
          const previous = queryClient.getQueryData<ConfigActivitiesResponse>(
            query.queryKey,
          );
          if (!previous || previous.node !== vars.node) continue;
          context.snapshots.push({ key: query.queryKey, previous });
          queryClient.setQueryData<ConfigActivitiesResponse>(query.queryKey, {
            ...previous,
            items: previous.items.map((row) =>
              codes.has(row.activity_code)
                ? { ...row, scope_verdict: vars.verdict }
                : row,
            ),
          });
        }
        return context;
      } catch (error) {
        release();
        throw error;
      }
    },
    onSuccess: (data, vars, context) => {
      if (vars.node === "org_structure") return;
      const rows =
        context?.snapshots.flatMap((snapshot) => snapshot.previous.items) ?? [];
      const known = new Set(rows.map((row) => row.activity_code));
      data.previousVerdicts = groupByPreviousVerdict(
        rows,
        data.affected
          .filter(
            (item) => item.item_type === "activity" && known.has(item.item_key),
          )
          .map((item) => item.item_key),
      );
    },
    onError: (_error, _vars, context) => {
      for (const snapshot of context?.snapshots ?? []) {
        queryClient.setQueryData(snapshot.key, snapshot.previous);
      }
    },
    onSettled: async (_data, _error, vars, context) => {
      try {
        if (!vars.projectId) return;
        const project = vars.projectId;
        const keys: QueryKey[] = [
          stageKeys.all(project),
          ["config-server", "org-structure", project],
          ["config-server", "sscui-tree", project],
          ["config-server", "tables", project],
          ["config-server", "cells", project],
          ["sscui-tree-activities", project],
          ["sscui-activity-tables", project],
          ["sscui-activity-records", project],
          ["migration-items", project],
          ["pipeline", project],
          ["config-extraction-status", project],
        ];
        // Falhar ao reler não desfaz uma gravação já confirmada pelo servidor.
        await Promise.allSettled(
          keys.map((key) => queryClient.invalidateQueries({ queryKey: key })),
        );
      } finally {
        context?.release();
      }
    },
  });
  const mutate: UseMutationResult<
    VerdictResult,
    unknown,
    VerdictMutation,
    VerdictContext
  >["mutate"] = (vars, options) =>
    mutation.mutate({ ...vars, projectId }, options);
  const mutateAsync: UseMutationResult<
    VerdictResult,
    unknown,
    VerdictMutation,
    VerdictContext
  >["mutateAsync"] = (vars, options) =>
    mutation.mutateAsync({ ...vars, projectId }, options);
  return { ...mutation, mutate, mutateAsync };
}

const extractionRefreshKey = (projectId: string | undefined) =>
  ["config-server", "extraction-refresh", projectId] as const;

/**
 * O botão e o "rodando" (P29). O refresh em running vence: o clique acabou de pôr isso no cache, e
 * o overview ainda não sabe. Fora de running vale o overview, que é relido no fim do refresh e
 * depois de um DE-PARA novo; o dado guardado do refresh fica velho, porque a consulta desliga.
 * Só sem o campo no overview valem os dados do refresh.
 */
export function extractionRefreshFlags(
  overview?: ConfigServerOverview,
  refresh?: ExtractionRefreshStatus,
): { available: boolean; running: boolean } {
  if (refresh?.refresh?.status === "running")
    return { available: refresh.available, running: true };
  const flags = overview?.extraction_refresh;
  if (flags) return { available: flags.available, running: flags.running };
  return { available: refresh?.available ?? false, running: false };
}

/**
 * Consulta de 3 s do refresh (spec 2026-10-29, D14): nenhuma chamada ao montar a tela. Liga com
 * `watch` (o overview diz que há refresh rodando) ou com o cache em running, que o clique em
 * "Extrair a última atualização" põe ali (em qualquer tela: o cache é do QueryClient). Fora
 * disso fica desligada: nem ao montar, nem ao voltar à tela, nem na invalidação de
 * ["config-server"]. Para sozinha quando sai de running e então relê o overview e o resumo.
 */
export function useExtractionRefresh(
  projectId: string | undefined,
  { watch }: { watch: boolean },
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: extractionRefreshKey(projectId),
    queryFn: () => getExtractionRefresh(projectId!),
    enabled: (q) =>
      !!projectId && (watch || q.state.data?.refresh?.status === "running"),
    refetchInterval: (q) =>
      q.state.data?.refresh?.status === "running" ? LIVE_INTERVAL_MS : false,
  });
  const status = query.data?.refresh?.status;
  const previous = useRef(status);
  const watching = useRef(watch);
  watching.current = watch;
  useEffect(() => {
    // Com watch ligado o overview disse running: um 1º resultado já terminado (previous ainda
    // indefinido) também é um fim, senão o overview velho segue com o spinner.
    const wasRunning =
      previous.current === "running" ||
      (previous.current === undefined && watching.current);
    if (wasRunning && status && status !== "running" && projectId) {
      void queryClient.invalidateQueries({
        queryKey: stageKeys.all(projectId),
      });
    }
    previous.current = status;
  }, [status, projectId, queryClient]);
  return query;
}

export function useStartExtractionRefresh(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => startExtractionRefresh(projectId!),
    onSuccess: (data) => qc.setQueryData(extractionRefreshKey(projectId), data),
    // 409 (outro usuário já está extraindo): relê o overview, que passa a dizer running e liga a
    // consulta desta tela.
    onError: () => {
      if (projectId)
        void qc.invalidateQueries({ queryKey: stageKeys.overview(projectId) });
    },
  });
}
